// ============================================================
// bankSoalInterpretasiBentuk.js — Modul "Interpretasi Bentuk"
// Latihan menyerupai Rorschach Inkblot Test: peserta melihat gambar
// abstrak simetris (seperti bercak tinta) lalu memilih pilihan ganda
// yang paling mendekati apa yang mereka lihat.
//
// CATATAN PENTING:
// - Seluruh ilustrasi SVG di bawah ini ORISINAL (blob abstrak digambar
//   sendiri & disimetriskan lewat transform mirror), BUKAN hasil
//   scan/tiru dari plate Rorschach asli manapun.
// - Rorschach ASLI tidak dijawab pilihan ganda (open-ended, dinilai
//   psikolog terlatih pakai sistem skoring seperti Exner). Versi
//   pilihan-ganda ini adalah adaptasi sederhana untuk latihan/simulasi
//   skala besar — TIDAK ada jawaban benar/salah, semua pilihan sah-sah
//   saja dipilih.
// - Skor "kecenderungan pola interpretasi" di bawah ini BUKAN alat
//   diagnosis psikologis/psikiatris — murni gambaran distribusi jenis
//   jawaban yang dipilih (imajinatif vs konkret vs bermuatan emosi),
//   disertai disclaimer non-klinis wajib di layar hasil.
// ============================================================

function svgBg(content) {
  return `<svg viewBox="0 0 200 140" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%"><rect width="200" height="140" fill="#F8FAFC" rx="8"/>${content}</svg>`;
}

// Blob simetris — path digambar cuma di separuh kiri (sumbu x=100),
// separuh kanan otomatis dicerminkan lewat transform, supaya bentuknya
// dijamin simetris persis (khas ink-blot) tanpa perlu hitung manual.
function blotSimetris(pathKiri, warna = '#312E81') {
  return svgBg(`
    <path d="${pathKiri}" fill="${warna}" opacity="0.85"/>
    <path d="${pathKiri}" fill="${warna}" opacity="0.85" transform="translate(200,0) scale(-1,1)"/>
  `);
}

// ─── Pool opsi jawaban ────────────────────────────────────────
// dimensi: 'imajinasi' | 'orientasi_emosi' | 'cara_pandang' — kategori
// non-klinis, cuma menandai KECENDERUNGAN JENIS jawaban yang dipilih,
// bukan penilaian benar/salah.
const OPSI_BERUANG      = { id: 'beruang', label: 'Wujud beruang', dimensi: 'cara_pandang' };
const OPSI_SERANGGA     = { id: 'serangga', label: 'Kepala serangga', dimensi: 'cara_pandang' };
const OPSI_BELALANG     = { id: 'belalang', label: 'Belalang', dimensi: 'cara_pandang' };

const OPSI_UNIK = {
  bercakDarah:  { id: 'bercak_darah', label: 'Bayangan bercak darah', dimensi: 'orientasi_emosi' },
  setanAsap:    { id: 'setan_asap', label: 'Setan (asap)', dimensi: 'imajinasi' },
  orangDuduk:   { id: 'orang_duduk', label: 'Orang berempat sedang duduk', dimensi: 'cara_pandang' },
  roh:          { id: 'roh', label: 'Roh, orang meninggal', dimensi: 'orientasi_emosi' },
  kupu:         { id: 'kupu', label: 'Kupu-kupu terbang', dimensi: 'imajinasi' },
  topeng:       { id: 'topeng', label: 'Topeng wajah', dimensi: 'cara_pandang' },
  ledakan:      { id: 'ledakan', label: 'Ledakan / kembang api', dimensi: 'imajinasi' },
  wajahMarah:   { id: 'wajah_marah', label: 'Wajah sedang marah', dimensi: 'orientasi_emosi' },
  peta:         { id: 'peta', label: 'Peta pulau/wilayah', dimensi: 'cara_pandang' },
  awanBadai:    { id: 'awan_badai', label: 'Awan mendung/badai', dimensi: 'orientasi_emosi' },
  burungTerbang:{ id: 'burung_terbang', label: 'Sepasang burung terbang berhadapan', dimensi: 'imajinasi' },
  kristal:      { id: 'kristal', label: 'Kristal / batu permata', dimensi: 'cara_pandang' },
  tarian:       { id: 'tarian', label: 'Dua orang sedang menari', dimensi: 'imajinasi' },
  pertarungan:  { id: 'pertarungan', label: 'Dua sosok sedang bertarung', dimensi: 'orientasi_emosi' },
  pohonAkar:    { id: 'pohon_akar', label: 'Pohon dengan akar menjalar', dimensi: 'cara_pandang' },
  malaikat:     { id: 'malaikat', label: 'Sosok bersayap / malaikat', dimensi: 'imajinasi' },
};

function opsiSoal(...unik) {
  // 3 opsi berulang + opsi unik untuk soal ini, urutan diacak per-render
  // (lihat getSoalInterpretasiBentuk) supaya tidak selalu di posisi sama.
  return [OPSI_BERUANG, OPSI_SERANGGA, OPSI_BELALANG, ...unik];
}

export const soalInterpretasiBentuk = [
  {
    id: 'IB-001',
    gambar: blotSimetris('M100,20 C70,18 45,35 42,55 C39,75 55,80 50,100 C46,118 65,130 100,128 Z', '#1E3A8A'),
    opsi: opsiSoal(OPSI_UNIK.bercakDarah, OPSI_UNIK.setanAsap, OPSI_UNIK.orangDuduk, OPSI_UNIK.roh),
  },
  {
    id: 'IB-002',
    gambar: blotSimetris('M100,12 C55,5 22,18 20,38 C18,58 35,65 45,80 C55,95 65,100 75,115 C85,128 95,124 100,124 Z', '#7C2D12'),
    opsi: opsiSoal(OPSI_UNIK.kupu, OPSI_UNIK.topeng, OPSI_UNIK.awanBadai, OPSI_UNIK.kristal),
  },
  {
    id: 'IB-003',
    gambar: blotSimetris('M100,20 L70,15 L60,35 L40,40 L55,60 L35,70 L50,85 L38,100 L58,110 L50,125 L100,130 Z', '#166534'),
    opsi: opsiSoal(OPSI_UNIK.ledakan, OPSI_UNIK.wajahMarah, OPSI_UNIK.peta, OPSI_UNIK.tarian),
  },
  {
    id: 'IB-004',
    gambar: blotSimetris('M100,15 L55,45 L60,70 L45,100 L70,125 L100,128 Z', '#831843'),
    opsi: opsiSoal(OPSI_UNIK.burungTerbang, OPSI_UNIK.pertarungan, OPSI_UNIK.pohonAkar, OPSI_UNIK.malaikat),
  },
  {
    id: 'IB-005',
    gambar: blotSimetris('M100,10 C85,8 75,15 74,30 C73,50 80,55 76,70 C72,90 80,100 78,115 C76,125 88,132 100,130 Z', '#3730A3'),
    opsi: opsiSoal(OPSI_UNIK.bercakDarah, OPSI_UNIK.kupu, OPSI_UNIK.roh, OPSI_UNIK.peta),
  },
  {
    id: 'IB-006',
    gambar: blotSimetris('M100,25 L80,5 C70,15 55,25 52,40 C49,58 62,62 55,78 C48,95 62,100 60,115 C65,125 85,122 100,122 Z', '#92400E'),
    opsi: opsiSoal(OPSI_UNIK.setanAsap, OPSI_UNIK.topeng, OPSI_UNIK.awanBadai, OPSI_UNIK.malaikat),
  },
  {
    id: 'IB-007',
    gambar: blotSimetris('M100,20 C90,15 80,20 78,35 C76,50 55,55 55,70 C55,85 76,90 78,105 C80,120 90,125 100,120 Z', '#134E4A'),
    opsi: opsiSoal(OPSI_UNIK.orangDuduk, OPSI_UNIK.ledakan, OPSI_UNIK.kristal, OPSI_UNIK.tarian),
  },
  {
    id: 'IB-008',
    gambar: blotSimetris('M100,15 L50,25 L45,60 L55,95 L40,125 L100,130 Z', '#701A75'),
    opsi: opsiSoal(OPSI_UNIK.roh, OPSI_UNIK.wajahMarah, OPSI_UNIK.pohonAkar, OPSI_UNIK.burungTerbang),
  },
  {
    id: 'IB-009',
    gambar: blotSimetris('M100,12 C68,5 42,18 42,38 C42,55 60,58 75,68 C60,78 40,82 40,105 C40,125 68,135 100,128 Z', '#1D4ED8'),
    opsi: opsiSoal(OPSI_UNIK.pertarungan, OPSI_UNIK.setanAsap, OPSI_UNIK.peta, OPSI_UNIK.malaikat),
  },
  {
    id: 'IB-010',
    gambar: blotSimetris('M100,25 C85,15 70,20 65,30 C60,20 45,25 42,38 C38,50 50,55 48,65 C44,78 55,82 52,95 C48,108 62,112 60,122 C70,130 88,126 100,126 Z', '#57534E'),
    opsi: opsiSoal(OPSI_UNIK.kupu, OPSI_UNIK.bercakDarah, OPSI_UNIK.kristal, OPSI_UNIK.awanBadai),
  },
];

// Acak urutan opsi tiap soal supaya 3 opsi "berulang" (beruang/serangga/
// belalang) tidak selalu di posisi sama — mengurangi bias pola menjawab.
function acakOpsi(soal) {
  return { ...soal, opsi: [...soal.opsi].sort(() => Math.random() - 0.5) };
}

export function getSoalInterpretasiBentuk() {
  return [...soalInterpretasiBentuk].sort(() => Math.random() - 0.5).map(acakOpsi);
}

export const URUTAN_DIMENSI_BENTUK = ['imajinasi', 'orientasi_emosi', 'cara_pandang'];
export const NAMA_DIMENSI_BENTUK = {
  imajinasi: { nama: 'Imajinasi', icon: '✨', deskripsi: 'Kecenderungan melihat sosok/aksi yang dinamis dan penuh khayalan pada bentuk ambigu.' },
  orientasi_emosi: { nama: 'Orientasi Emosi', icon: '💭', deskripsi: 'Kecenderungan memaknai bentuk ambigu lewat asosiasi yang bermuatan emosi/perasaan.' },
  cara_pandang: { nama: 'Cara Pandang', icon: '🔍', deskripsi: 'Kecenderungan memaknai bentuk ambigu lewat objek/figur yang konkret dan familiar.' },
};

// Skor "kecenderungan pola interpretasi" — BUKAN benar/salah (tidak ada
// jawaban benar di tes proyektif). dimensiPct adalah DISTRIBUSI proporsi
// pilihan (ketiganya berjumlah ~100% kalau semua soal dijawab), bukan 3
// skala independen 0-100 seperti modul Kestabilan.
export function hitungSkorInterpretasiBentuk(soalList, jawabanUser) {
  const byId = {};
  jawabanUser.forEach(j => { byId[j.id] = j.jawaban; });

  const hitung = { imajinasi: 0, orientasi_emosi: 0, cara_pandang: 0 };
  let totalDijawab = 0;

  soalList.forEach(soal => {
    const pilihanId = byId[soal.id];
    if (!pilihanId) return;
    const opsi = soal.opsi.find(o => o.id === pilihanId);
    if (!opsi) return;
    hitung[opsi.dimensi] = (hitung[opsi.dimensi] || 0) + 1;
    totalDijawab += 1;
  });

  const dimensiPct = {};
  URUTAN_DIMENSI_BENTUK.forEach(d => {
    dimensiPct[d] = totalDijawab > 0 ? Math.round((hitung[d] / totalDijawab) * 100) : null;
  });

  const pctSelesai = Math.round((totalDijawab / soalList.length) * 100);

  // "Skor" modul (dipakai jadi skor akumulasi Tryout) = persentase
  // kelengkapan pengisian — konsisten dengan modul Interpretasi Gambar,
  // supaya tidak ada kesan menilai benar/salah pada tes proyektif ini.
  return { dimensiPct, hitung, totalDijawab, totalSoal: soalList.length, pctSelesai, skor: pctSelesai };
}

export const DISCLAIMER_INTERPRETASI_BENTUK = 'Hasil ini adalah gambaran distribusi pola jawaban dari latihan simulasi, BUKAN diagnosis atau penilaian psikologis/psikiatris resmi. Rorschach asli dijawab bebas (bukan pilihan ganda) dan dinilai psikolog terlatih — versi ini cuma latihan membiasakan format soal.';

// ===== SOAL TAMBAHAN BARU (Interpretasi Bentuk) =====
// Soal lama tetap dipertahankan. 10 bercak abstrak simetris ORISINAL baru
// (bentuk berbeda satu sama lain dan dari IB-001..010), memakai pool opsi yang sama.
soalInterpretasiBentuk.push(
  {
    id: 'IB-011',
    gambar: blotSimetris('M100,18 C80,10 58,22 52,42 C46,62 70,70 64,88 C58,106 40,112 46,124 C60,134 85,130 100,128 Z', '#0F766E'),
    opsi: opsiSoal(OPSI_UNIK.kupu, OPSI_UNIK.pohonAkar, OPSI_UNIK.bercakDarah, OPSI_UNIK.topeng),
  },
  {
    id: 'IB-012',
    gambar: blotSimetris('M100,50 C85,20 40,8 25,30 C12,50 40,64 60,66 C35,76 25,100 45,116 C65,130 90,100 100,92 Z', '#9D174D'),
    opsi: opsiSoal(OPSI_UNIK.malaikat, OPSI_UNIK.burungTerbang, OPSI_UNIK.awanBadai, OPSI_UNIK.wajahMarah),
  },
  {
    id: 'IB-013',
    gambar: blotSimetris('M100,10 L84,34 L60,22 L66,48 L38,52 L62,68 L44,92 L72,90 L70,120 L100,104 Z', '#7F1D1D'),
    opsi: opsiSoal(OPSI_UNIK.ledakan, OPSI_UNIK.kristal, OPSI_UNIK.pertarungan, OPSI_UNIK.setanAsap),
  },
  {
    id: 'IB-014',
    gambar: blotSimetris('M100,8 C92,8 88,20 90,34 C70,36 60,50 66,62 C72,74 88,72 88,86 C70,92 62,104 70,116 C78,128 92,130 100,128 Z', '#1E40AF'),
    opsi: opsiSoal(OPSI_UNIK.orangDuduk, OPSI_UNIK.roh, OPSI_UNIK.pohonAkar, OPSI_UNIK.tarian),
  },
  {
    id: 'IB-015',
    gambar: blotSimetris('M100,50 C70,30 24,38 14,62 C6,80 30,94 52,92 C70,90 82,104 100,106 Z', '#4D7C0F'),
    opsi: opsiSoal(OPSI_UNIK.peta, OPSI_UNIK.awanBadai, OPSI_UNIK.burungTerbang, OPSI_UNIK.bercakDarah),
  },
  {
    id: 'IB-016',
    gambar: blotSimetris('M100,22 C78,22 66,34 70,50 C74,64 54,68 44,82 C34,96 46,114 64,118 C80,122 92,112 100,112 Z', '#B45309'),
    opsi: opsiSoal(OPSI_UNIK.wajahMarah, OPSI_UNIK.kristal, OPSI_UNIK.tarian, OPSI_UNIK.malaikat),
  },
  {
    id: 'IB-017',
    gambar: blotSimetris('M100,14 C86,6 70,16 74,28 C58,24 46,38 56,50 C40,54 36,72 52,78 C44,92 56,108 72,104 C74,120 90,128 100,122 Z', '#475569'),
    opsi: opsiSoal(OPSI_UNIK.awanBadai, OPSI_UNIK.setanAsap, OPSI_UNIK.peta, OPSI_UNIK.kupu),
  },
  {
    id: 'IB-018',
    gambar: blotSimetris('M100,40 C86,10 50,6 30,22 C20,34 36,48 52,46 C40,62 46,84 64,90 C74,94 80,104 78,118 C90,124 96,116 100,112 Z', '#6D28D9'),
    opsi: opsiSoal(OPSI_UNIK.topeng, OPSI_UNIK.ledakan, OPSI_UNIK.pertarungan, OPSI_UNIK.roh),
  },
  {
    id: 'IB-019',
    gambar: blotSimetris('M100,12 C80,12 70,30 76,48 C80,60 90,66 90,70 C90,76 78,82 70,96 C62,112 72,130 100,130 Z', '#0E7490'),
    opsi: opsiSoal(OPSI_UNIK.orangDuduk, OPSI_UNIK.kupu, OPSI_UNIK.malaikat, OPSI_UNIK.pohonAkar),
  },
  {
    id: 'IB-020',
    gambar: blotSimetris('M100,16 C76,12 52,24 46,44 C42,58 56,64 54,76 C52,90 38,98 44,114 C54,132 82,130 100,126 Z', '#86198F'),
    opsi: opsiSoal(OPSI_UNIK.bercakDarah, OPSI_UNIK.tarian, OPSI_UNIK.setanAsap, OPSI_UNIK.kristal),
  },
);
